import { FlowType } from "@redotech/redo-model/return-flow";
import { Team } from "@redotech/redo-model/team";
import { Step, StepId } from "@redotech/redo-return-ui/flows/step";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { Text } from "@redotech/redo-web/text";
import { Handle, Node, NodeProps, Position, useViewport } from "@xyflow/react";
import classNames from "classnames";
import { memo, useMemo } from "react";
import * as styles from "./flow-step-node.module.css";

const NODE_WIDTH = 224;
const HANDLE_VISUAL_SIZE = 8;
const HANDLE_HOVER_SIZE = 32;
export const ZOOM_THRESHOLD_COMBINE = 0.75;

export type NodeData = {
  step: Step;
  stepId: StepId;
  flowType: FlowType;
  nodePositions: Map<string, { x: number; y: number }>;
  allSteps: Map<string, Step>;
  team?: Team;
  pathHighlighted?: boolean;
  isCurrentStep?: boolean;
};

export const FlowStepNode = memo(function FlowStepNode({
  data,
  selected: nodeSelected,
}: NodeProps<Node<NodeData>>) {
  const {
    step,
    stepId,
    flowType,
    nodePositions,
    allSteps,
    team,
    pathHighlighted,
    isCurrentStep,
  } = data;

  const { zoom } = useViewport();

  const selected = nodeSelected;
  const layout = step.layout();
  const isCompact = layout === BlockLayout.COMPACT;
  const nodeWidth = NODE_WIDTH;
  const downstreams = step.downstream();
  const maxConnections = step.maxDownstream();
  const canBeTarget = stepId !== "start";

  const shouldCombineHandles =
    zoom < ZOOM_THRESHOLD_COMBINE && maxConnections > 2;

  const downstreamBySemanticIndex = useMemo(() => {
    if (!nodePositions || !allSteps) {
      return new Map(
        downstreams.map((downstream, index) => [index, downstream]),
      );
    }

    return new Map(
      downstreams
        .map((downstream, index) => ({ downstream, index }))
        .filter(
          ({ downstream }) => downstream.id && allSteps.has(downstream.id),
        )
        .map(({ downstream, index }) => [index, downstream]),
    );
  }, [downstreams, nodePositions, allSteps]);

  return (
    <>
      {canBeTarget && (
        <Handle
          position={Position.Top}
          style={{
            position: "absolute",
            top: "-28px",
            left: "50%",
            transform: "translateX(-50%)",
            width: `${HANDLE_HOVER_SIZE}px`,
            height: `${HANDLE_HOVER_SIZE}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            background: "transparent",
            border: "none",
            opacity: 0,
            cursor: "crosshair",
          }}
          type="target"
        />
      )}

      <Flex
        className={classNames(
          styles.nodeCard,
          {
            "border border-red-500": !step.valid(flowType),
            "border border-gray-700": selected,
            "border border-gray-300": !selected && step.valid(flowType),
          },
          pathHighlighted && styles.pathHighlighted,
          !pathHighlighted && isCurrentStep && styles.currentStep,
        )}
        dir="column"
        gap="md"
        style={{
          width: stepId === "start" ? "unset" : nodeWidth,
          maxWidth: nodeWidth,
        }}
      >
        <Flex align="center" gap="sm" justify="space-between">
          <Flex
            align="center"
            gap="sm"
            justify="flex-start"
            style={{ flex: 1, minWidth: 0 }}
          >
            <RedoFeaturedIcon
              color={stepId === "start" ? "black" : step.iconColor || "warning"}
              Icon={step.Icon}
              size="xs"
              type={stepId === "start" ? "high-contrast" : "default"}
            />
            <Text
              fontSize="sm"
              fontWeight="medium"
              style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}
            >
              {step.sidebarTitle?.() !== step.title()
                ? step.sidebarTitle?.()
                : step.title()}
            </Text>
          </Flex>
        </Flex>

        {stepId !== "start" && (
          <Text
            style={{
              fontSize: "12px",
              color: "#6b7280",
              wordBreak: "break-word",
              lineHeight: "1.3",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: isCompact ? 2 : 3,
              WebkitBoxOrient: "vertical",
            }}
          >
            {step.description(team, flowType)}
          </Text>
        )}

        {!step.valid(flowType) && (
          <Text
            style={{
              fontSize: "11px",
              color: "#ef4444",
              fontWeight: "500",
              marginTop: "4px",
            }}
          >
            Invalid configuration
          </Text>
        )}
      </Flex>

      {Array.from({ length: maxConnections }, (_, handleIndex) => {
        const spacing = maxConnections > 1 ? 0.8 / (maxConnections - 1) : 0;
        const startOffset = maxConnections > 1 ? 0.1 : 0.5;
        const individualPosition =
          maxConnections === 1
            ? "50%"
            : `${(startOffset + spacing * handleIndex) * 100}%`;

        const leftPosition = shouldCombineHandles ? "50%" : individualPosition;

        const correspondingDownstream =
          downstreamBySemanticIndex.get(handleIndex);
        const handleId = correspondingDownstream?.id
          ? `${stepId}-${correspondingDownstream.id}-${handleIndex}`
          : `${stepId}-placeholder-${handleIndex}`;

        const showVisualHandle = !shouldCombineHandles;

        return (
          <Handle
            id={handleId}
            key={handleId}
            position={Position.Bottom}
            style={{
              position: "absolute",
              bottom: "-28px",
              left: leftPosition,
              transform: "translateX(-50%)",
              width: `${HANDLE_HOVER_SIZE}px`,
              height: `${HANDLE_HOVER_SIZE}px`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1,
              background: "transparent",
              border: "none",
              opacity: showVisualHandle ? 1 : 0,
              pointerEvents: showVisualHandle ? "auto" : "none",
              cursor: "crosshair",
            }}
            type="source"
          >
            <div
              style={{
                background: "white",
                width: `${HANDLE_VISUAL_SIZE}px`,
                height: `${HANDLE_VISUAL_SIZE}px`,
                border: "2px solid black",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                borderRadius: "50%",
                pointerEvents: "none",
              }}
            />
          </Handle>
        );
      })}

      {shouldCombineHandles && (
        <div
          style={{
            position: "absolute",
            bottom: "-28px",
            left: "50%",
            transform: "translateX(-50%)",
            width: `${HANDLE_HOVER_SIZE}px`,
            height: `${HANDLE_HOVER_SIZE}px`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "white",
              width: `${HANDLE_VISUAL_SIZE}px`,
              height: `${HANDLE_VISUAL_SIZE}px`,
              border: "2px solid black",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
              borderRadius: "50%",
            }}
          />
        </div>
      )}
    </>
  );
});
